import type {
  OverviewConditionRow,
  OverviewDecisionSummary,
  OverviewPeriodConditionRow,
} from '../continuousSafety/overviewSiteConditionSummary';
import {
  OVER_TIME_WINDOW_OPTIONS,
  type OverTimeWindowId,
} from '../sellableSiteExperience/overTimePresenter';
import {
  controlPolicyStage,
  currentActivityStage,
  currentPolicyStage,
  recurringOccurrenceChart,
  type OverviewV2RecurringChart,
  type OverviewV2Stage,
} from '../../pages/overviewV2Presentation';
import { overviewV2OccurrenceHeadline } from '../../pages/overviewV2CustomerCopy';
import {
  SHOWCASE_ROOM_B_PROXIMITY_CONDITION_ID,
  SHOWCASE_ROOM_B_RAE_CONDITION_ID,
} from './showcasePresentation';
import type { ShowcaseRoomBCondition, ShowcaseSource } from './showcaseTrustPolicy';

export type ShowcaseConditionPresentation = {
  conditionId: string;
  cameraId: string | null;
  qualitativeValue: string;
  lastCompletedValue: string | null;
  lastCompletedDetail: string | null;
  /** Set when the count is shown from a delayed or source-lagged window. */
  freshnessNote?: string | null;
  /** True only when neither the reviewed claim nor a completed PT15M bucket has a value. */
  nowMeasurementUnavailable?: boolean;
  /** True when condition_lifecycle was present on the period row. */
  lifecycleAvailable?: boolean;
  recurring: OverviewV2RecurringChart;
  recordedOccurrenceTotal: number | null;
  usableHours: number | null;
  control: OverviewV2Stage;
};

export type ShowcaseConditionBundle = {
  siteTimezone: string | null;
  selectedWindowId: OverTimeWindowId;
  boothRae: ShowcaseConditionPresentation | null;
  roomBRae: ShowcaseConditionPresentation | null;
  roomBProximity: ShowcaseConditionPresentation | null;
};

export const SHOWCASE_OLDER_READING = 'Older reading';

function text(value: unknown): string {
  return String(value || '').trim();
}

function finiteCount(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** Delayed freshness or source lag. Does not erase a completed count. */
export function showcaseReadingIsOlder(args: {
  displayState?: string | null;
  reasonCodes?: string[] | null;
}): boolean {
  const state = text(args.displayState).toLowerCase();
  if (state === 'delayed') return true;
  return (args.reasonCodes || []).some((code) => text(code).includes('source_lag_exceeds_policy'));
}

export function showcaseRepeatingHasHistoricalData(
  condition: ShowcaseConditionPresentation | null | undefined,
): boolean {
  if (!condition) return false;
  if (condition.recurring.buckets.some((bucket) => (
    typeof bucket.occurrences === 'number' && Number.isFinite(bucket.occurrences)
  ))) {
    return true;
  }
  if (finiteCount(condition.recordedOccurrenceTotal) != null) return true;
  return Boolean(text(condition.recurring.primaryValue));
}

/**
 * Shared fetch failure with no bundle stays unavailable.
 * A delayed window must not hide a completed count, historical buckets, or lifecycle.
 */
export function showcaseRoomBAvailability(args: {
  status?: string | null;
  condition: ShowcaseConditionPresentation | null | undefined;
}): { nowUnavailable: boolean; repeatingUnavailable: boolean; verifyUnavailable: boolean } {
  const status = text(args.status);
  const condition = args.condition;
  const fetchFailedWithoutBundle = status === 'unavailable' && !condition;
  return {
    nowUnavailable: fetchFailedWithoutBundle || Boolean(condition?.nowMeasurementUnavailable),
    repeatingUnavailable: fetchFailedWithoutBundle
      || (status === 'unavailable' && !showcaseRepeatingHasHistoricalData(condition)),
    verifyUnavailable: fetchFailedWithoutBundle
      || (status === 'unavailable' && !condition?.lifecycleAvailable),
  };
}

function sameId(left: unknown, right: unknown): boolean {
  return text(left).toLowerCase() === text(right).toLowerCase();
}

function sameCamera(left: unknown, right: unknown): boolean {
  return Boolean(text(left) && text(right) && text(left) === text(right));
}

export function selectOverviewConditionRow(
  rows: OverviewConditionRow[] | null | undefined,
  conditionId: string,
): OverviewConditionRow | null {
  const wanted = text(conditionId);
  if (!wanted) return null;
  return (rows || []).find((row) => sameId(row.conditionId, wanted)) || null;
}

export function selectOverviewPeriodRow(
  rows: OverviewPeriodConditionRow[] | null | undefined,
  conditionId: string,
): OverviewPeriodConditionRow | null {
  const wanted = text(conditionId);
  if (!wanted) return null;
  return (rows || []).find((row) => sameId(row.conditionId, wanted)) || null;
}

export function selectBoothRaeConditionId(args: {
  rows: OverviewConditionRow[] | null | undefined;
  boothCameraId?: string | null;
  roomBCameraId?: string | null;
}): string | null {
  const boothCamera = text(args.boothCameraId);
  const roomBCamera = text(args.roomBCameraId);
  const rows = args.rows || [];
  if (boothCamera) {
    const matched = rows.find((row) => sameCamera(row.cameraId, boothCamera));
    if (matched) return text(matched.conditionId) || null;
  }
  const notRoomB = rows.find((row) => {
    const camera = text(row.cameraId);
    if (roomBCamera && camera === roomBCamera) return false;
    return Boolean(camera);
  });
  return text(notRoomB?.conditionId) || null;
}

function presentControl(
  current: OverviewConditionRow | null,
  recurring: OverviewPeriodConditionRow | null,
): OverviewV2Stage {
  const lifecycle = recurring?.lifecycle;
  const since = current?.sinceControl || recurring?.sinceControl;
  const linked = Boolean(lifecycle?.controlRevisionId || since?.controlId);
  return controlPolicyStage({
    linkedToControl: linked,
    controlId: lifecycle?.controlRevisionId || since?.controlId || null,
    controlDateLabel: lifecycle?.sinceStartedAt || since?.anchorStartedAt,
    controlName: linked ? 'Recorded control' : null,
    recordedStatusLabel: linked ? 'Linked through condition lifecycle' : null,
    followUpState: since?.comparisonStatus || null,
  });
}

export function presentShowcaseCondition(args: {
  current: OverviewConditionRow | null;
  recurring: OverviewPeriodConditionRow | null;
  summary: OverviewDecisionSummary | null;
  windowId?: OverTimeWindowId | null;
}): ShowcaseConditionPresentation | null {
  const conditionId = text(args.current?.conditionId || args.recurring?.conditionId);
  if (!conditionId) return null;
  const timeZone = args.summary?.siteTimezone || null;
  const qualitative = currentActivityStage(args.current?.currentActivity, { timeZone });
  const reviewedStage = currentPolicyStage(args.current, {
    queryAsOf: args.summary?.queryAsOf,
    measuredWindow: args.summary?.now.measuredWindow,
    timeZone,
  });
  const reviewed = finiteCount(args.current?.measurementValue);
  const completedBucket = finiteCount(args.current?.latestCompletedPt15mValue);
  const older = showcaseReadingIsOlder({
    displayState: args.current?.nowWindowDisplayState,
    reasonCodes: args.current?.nowWindowReasonCodes,
  });
  const freshnessNote = older ? SHOWCASE_OLDER_READING : null;
  let lastCompletedValue: string | null = reviewedStage.value;
  let lastCompletedDetail: string | null = reviewedStage.detail;
  let nowMeasurementUnavailable = false;
  if (reviewed != null) {
    lastCompletedValue = reviewedStage.value;
    lastCompletedDetail = reviewedStage.detail;
  } else if (completedBucket != null) {
    lastCompletedValue = overviewV2OccurrenceHeadline(completedBucket);
    lastCompletedDetail = null;
  } else {
    lastCompletedValue = null;
    lastCompletedDetail = null;
    nowMeasurementUnavailable = true;
  }
  const windowId = args.windowId || args.summary?.overTime.selectedWindowId || 'rolling_24h';
  // Historical period buckets stay on the card when the current window is delayed
  // or stale. Overview's coverage rate label can be null for that reason; Showcase
  // does not use it to hide REPEATING.
  const recurring = recurringOccurrenceChart(args.recurring, { windowId, timeZone });
  return {
    conditionId,
    cameraId: args.current?.cameraId || args.recurring?.cameraId || null,
    qualitativeValue: qualitative.value,
    lastCompletedValue,
    lastCompletedDetail,
    freshnessNote: nowMeasurementUnavailable ? null : freshnessNote,
    nowMeasurementUnavailable,
    lifecycleAvailable: Boolean(args.recurring?.lifecycle),
    recurring,
    recordedOccurrenceTotal: args.recurring?.recordedOccurrenceTotal ?? null,
    usableHours: args.recurring?.usableHours ?? null,
    control: presentControl(args.current, args.recurring),
  };
}

export function assembleShowcaseConditionBundle(args: {
  summary: OverviewDecisionSummary | null;
  boothCameraId?: string | null;
  roomBCameraId?: string | null;
  roomBRaeConditionId?: string | null;
  roomBProximityConditionId?: string | null;
  selectedWindowId?: OverTimeWindowId | null;
}): ShowcaseConditionBundle | null {
  if (!args.summary) return null;
  const raeId = text(args.roomBRaeConditionId) || SHOWCASE_ROOM_B_RAE_CONDITION_ID;
  const proxId = text(args.roomBProximityConditionId) || SHOWCASE_ROOM_B_PROXIMITY_CONDITION_ID;
  const nowRows = args.summary.now.conditionRows || [];
  const periodRows = args.summary.overTime.conditionRows || [];
  const boothId = selectBoothRaeConditionId({
    rows: nowRows,
    boothCameraId: args.boothCameraId,
    roomBCameraId: args.roomBCameraId,
  });
  const windowId = args.selectedWindowId || args.summary.overTime.selectedWindowId || 'rolling_24h';
  const present = (conditionId: string | null) => presentShowcaseCondition({
    current: conditionId ? selectOverviewConditionRow(nowRows, conditionId) : null,
    recurring: conditionId ? selectOverviewPeriodRow(periodRows, conditionId) : null,
    summary: args.summary,
    windowId,
  });
  return {
    siteTimezone: args.summary.siteTimezone || null,
    selectedWindowId: windowId,
    boothRae: present(boothId),
    roomBRae: present(raeId),
    roomBProximity: present(proxId),
  };
}

export function selectedShowcaseCondition(
  bundle: ShowcaseConditionBundle | null,
  source: ShowcaseSource,
  roomBCondition: ShowcaseRoomBCondition,
): ShowcaseConditionPresentation | null {
  if (!bundle) return null;
  if (source === 'booth') return bundle.boothRae;
  return roomBCondition === 'proximity' ? bundle.roomBProximity : bundle.roomBRae;
}

export const SHOWCASE_VERIFY_NO_CONTROL = 'No control recorded';
export const SHOWCASE_VERIFY_RECORD_CONTROL =
  'Record a control to begin before/after verification.';
export const SHOWCASE_HISTORY_BUILDING = 'History building';
export const SHOWCASE_HISTORY_LOADING = 'History loading';
export const SHOWCASE_MEASUREMENT_UNAVAILABLE = 'Measurement unavailable';
export const SHOWCASE_MEASUREMENT_LOADING = 'Loading measurement…';
export const SHOWCASE_PROX_QUANTITATIVE_UNDER_VALIDATION = 'Quantitative recurrence under validation';
export const SHOWCASE_RECENT_EVIDENCE = 'RECENT EVIDENCE';
export const SHOWCASE_RECENT_PROXIMITY_EVIDENCE = SHOWCASE_RECENT_EVIDENCE;
export const SHOWCASE_RAE_OBSERVED = 'Restricted-area entry observed';
export const SHOWCASE_PROX_OBSERVED = 'Proximity observed';
export const SHOWCASE_PROX_EVIDENCE_LOADING = 'Recent evidence loading…';
export const SHOWCASE_PROX_EVIDENCE_EMPTY = 'No recent evidence snapshot available';
export const SHOWCASE_EVIDENCE_IMAGE_HEIGHT_PX = 64;
export const SHOWCASE_GRAPH_MIN_HEIGHT_PX = 48;
export const SHOWCASE_EVIDENCE_IMAGE_HEIGHT = 'clamp(48px, 5.5vh, 64px)';
export const SHOWCASE_GRAPH_MIN_HEIGHT = 'clamp(36px, 4.5vh, 52px)';
export const SHOWCASE_ROOM_B_ONE_CAMERA_TWO = 'ONE CAMERA · TWO CONDITIONS';
export const SHOWCASE_ROOM_B_VIDEO_MAX_WIDTH = 'min(100%, calc(50dvh * 16 / 9))';
export const SHOWCASE_ROOM_B_VIDEO_OBJECT_FIT = 'contain';
export const SHOWCASE_ROOM_B_VIDEO_ASPECT = '16 / 9';
export const SHOWCASE_ROOM_B_GRID_ROWS = 'auto minmax(0, 1fr)';
export const SHOWCASE_ROOM_B_GRID_ROWS_SHORT = 'auto minmax(0, 1fr)';
export const SHOWCASE_SHORT_VIEWPORT = '@media (max-height: 1024px)';
/** Room B lower row: equal RAE and proximity columns. */
export const SHOWCASE_ROOM_B_LOWER_COLUMNS = 'minmax(0, 1fr) minmax(0, 1fr)';
export const SHOWCASE_TYPE = {
  conditionTitle: 22,
  primaryState: 28,
  primaryNumeric: 40,
  sectionHeading: 16,
  body: 16,
  meta: 13,
  summaryTitle: 26,
  summaryCondition: 18,
  summaryScope: 16,
} as const;
export const SHOWCASE_PROX_RECURRING_UNSHOWN = 'Quantitative recurrence not shown in this demo';
export const SHOWCASE_PROX_RECURRING_SUPPORT =
  'This condition is shown on the recorded scenario; quantitative recurrence is being validated.';
export const SHOWCASE_PROX_SCOPE_COPY = 'Measurement scope · Entire camera view';
export const SHOWCASE_RAE_SCOPE_COPY = 'Measurement scope · Zone';
export const SHOWCASE_RAE_VIDEO_SCOPE = 'Restricted-area entry · Zone measurement';
export const SHOWCASE_PROX_VIDEO_SCOPE = 'Forklift-pedestrian proximity · Entire camera view';
export const SHOWCASE_LAST_COMPLETED_HEADING = 'Last completed 15 min';
export const SHOWCASE_ROOM_B_SECONDARY = 'Warehouse walkway';
export const SHOWCASE_ROOM_B_SCENARIO = 'Recorded industrial scenario';
export const SHOWCASE_CONDITION_RAE_LABEL = 'Restricted-area entry';
export const SHOWCASE_CONDITION_PROXIMITY_LABEL = 'Forklift-pedestrian proximity';
export const SHOWCASE_HISTORY_AVAILABLE = 'History available';
export const SHOWCASE_STORY_NOW = 'NOW';
export const SHOWCASE_STORY_REPEATING = 'REPEATING';
export const SHOWCASE_STORY_REPEATING_RECORDED = 'REPEATING · RECORDED DEMO';
export const SHOWCASE_RECORDED_REPLAY_PATTERN = 'Pattern generated from replayed demo activity';
export const SHOWCASE_SUPPORTING_EVIDENCE = 'SUPPORTING EVIDENCE';
export const SHOWCASE_SUPPORTING_EVIDENCE_RECORDED = 'SUPPORTING EVIDENCE · RECORDED DEMO';
export const SHOWCASE_SUPPORTING_EVIDENCE_EMPTY = 'No supporting evidence available for this interval';
export const SHOWCASE_STORY_RECENT_EVIDENCE = SHOWCASE_RECENT_EVIDENCE;
export const SHOWCASE_STORY_VERIFY = 'VERIFY';
export const SHOWCASE_STORY_NOW_QUESTION = 'What is happening?';
export const SHOWCASE_STORY_REPEATING_QUESTION = 'What keeps happening?';
export const SHOWCASE_STORY_VERIFY_QUESTION = 'Did it change after action?';
export const SHOWCASE_VERIFY_COMPARE_COPY = 'Compare before and after a safety change.';
export const SHOWCASE_VIEW_SUPPORTING_EVIDENCE = 'View supporting evidence';
export const SHOWCASE_ROOM_B_CARD_PADDING = { xs: '20px', lg: '22px' } as const;
export const SHOWCASE_ROOM_B_ZONE_STROKE = '#22d3ee';
export const SHOWCASE_ROOM_B_ZONE_FILL = 'rgba(34, 211, 238, 0.05)';
export const SHOWCASE_ROOM_B_ZONE_STROKE_WIDTH_PX = 2.5;
export const SHOWCASE_ROOM_B_TYPE = {
  title: { xs: 24, lg: 26 },
  subtitle: { xs: 16, lg: 17 },
  section: { xs: 14, lg: 15 },
  now: { xs: 26, lg: 28 },
  peak: { xs: 24, lg: 26 },
  supporting: { xs: 16, lg: 17 },
} as const;
export const SHOWCASE_MOST_FREQUENT_PREFIX = 'Highest occurrence rate:';
export const SHOWCASE_LAST_COMPLETED_LINE_SUFFIX = 'last completed 15 min';
export const SHOWCASE_OCCURRENCE_TOTAL_UNAVAILABLE = 'Occurrence total unavailable';

export function showcaseLastCompletedLine(value: string | null | undefined): string {
  const trimmed = String(value || '').trim();
  if (!trimmed) return SHOWCASE_OCCURRENCE_TOTAL_UNAVAILABLE;
  return `${trimmed} · ${SHOWCASE_LAST_COMPLETED_LINE_SUFFIX}`;
}

export function showcasePeakRangeLabel(peakLabel: string | null | undefined): string | null {
  const stripped = String(peakLabel || '').replace(/^Peak:\s*/i, '').trim();
  return stripped || null;
}

/** Highest sum of normalized hourly rates, not the highest raw count. */
export function showcaseMostFrequentLabel(peakLabel: string | null | undefined): string | null {
  const stripped = showcasePeakRangeLabel(peakLabel);
  if (!stripped) return null;
  return `${SHOWCASE_MOST_FREQUENT_PREFIX} ${stripped}`;
}

/**
 * Recorded demo does not present the replay clock range as an operating pattern.
 * Live mode uses the same peak label that selected the evidence interval.
 */
export function showcaseSupportingEvidenceTitle(
  peakLabel: string | null | undefined,
  options?: { recordedReplay?: boolean },
): string {
  if (options?.recordedReplay) return SHOWCASE_SUPPORTING_EVIDENCE_RECORDED;
  const range = showcasePeakRangeLabel(peakLabel);
  if (!range) return SHOWCASE_SUPPORTING_EVIDENCE;
  return `${SHOWCASE_SUPPORTING_EVIDENCE} · ${range}`;
}

export function showcaseRepeatingRateLine(
  chart: Pick<OverviewV2RecurringChart, 'primaryValue' | 'primaryUnit'> | null | undefined,
): string | null {
  const value = String(chart?.primaryValue || '').trim();
  if (!value) return null;
  const unit = String(chart?.primaryUnit || 'occurrences per usable hour').trim();
  return `${value} ${unit}`;
}

export function showcaseRepeatingWindowLabel(windowId?: string | null): string | null {
  const id = String(windowId || '').trim();
  if (!id) return null;
  return OVER_TIME_WINDOW_OPTIONS.find((option) => option.id === id)?.label || null;
}

export function showcaseRepeatingWindowId(args: {
  selectedWindowId?: string | null;
  recurringWindowId?: string | null;
}): string | null {
  const dataWindow = String(args.recurringWindowId || '').trim();
  const queryWindow = String(args.selectedWindowId || '').trim();
  return dataWindow || queryWindow || null;
}
